import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  format,
  startOfDay,
  endOfDay,
  addDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addMonths,
  eachDayOfInterval,
  isSameMonth,
  isToday,
} from "date-fns";
import clsx from "clsx";
import {
  HiOutlinePlus,
  HiOutlineViewList,
  HiOutlineCalendar,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineLocationMarker,
  HiOutlineEye,
} from "react-icons/hi";
import Layout from "../../components/Layout/Layout";
import UserLayout from "../../components/Layout/UserLayout";
import {
  Card,
  Button,
  Badge,
  Select,
  SearchableSelect,
  SearchInput,
  Spinner,
  EmptyState,
  Tabs,
  Tooltip,
  stickyActionCellClass,
  stickyActionHeaderClass,
} from "../../components/UI";
import { Pagination } from "../../components/UI/Pagination";
import { visitsAPI, usersAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import VisitModal from "./VisitModal";
import VisitDetailModal from "./VisitDetailModal";
import {
  VISIT_TYPES,
  VISIT_TYPE_MAP,
  VISIT_STATUSES,
  fullName,
  formatMinutes,
  plannedMinutes,
} from "./visitConstants";

// Tab → status filter sent to the API
const STATUS_TABS = [
  { value: "all", label: "All", statuses: [] },
  { value: "pending", label: "Pending Approval", statuses: ["pending_approval"], adminOnly: true },
  { value: "upcoming", label: "Upcoming", statuses: ["scheduled"] },
  { value: "in_progress", label: "In Progress", statuses: ["in_progress"] },
  { value: "completed", label: "Completed", statuses: ["completed"] },
  { value: "missed", label: "No Show", statuses: ["no_show"] },
  { value: "rescheduled", label: "Rescheduled", statuses: ["rescheduled"] },
  { value: "closed", label: "Cancelled / Rejected", statuses: ["cancelled", "rejected"] },
];

const DATE_RANGES = [
  { value: "", label: "All Dates" },
  { value: "today", label: "Today" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "week", label: "This Week" },
  { value: "next7", label: "Next 7 Days" },
  { value: "last7", label: "Last 7 Days" },
  { value: "month", label: "This Month" },
];

const rangeFor = (key) => {
  const now = new Date();
  switch (key) {
    case "today":
      return [startOfDay(now), endOfDay(now)];
    case "tomorrow":
      return [startOfDay(addDays(now, 1)), endOfDay(addDays(now, 1))];
    case "week":
      return [startOfWeek(now, { weekStartsOn: 1 }), endOfWeek(now, { weekStartsOn: 1 })];
    case "next7":
      return [startOfDay(now), endOfDay(addDays(now, 6))];
    case "last7":
      return [startOfDay(addDays(now, -6)), endOfDay(now)];
    case "month":
      return [startOfMonth(now), endOfMonth(now)];
    default:
      return [null, null];
  }
};

const VisitsList = () => {
  const { isAdmin, isTelecaller } = useAuth();
  const PageLayout = isTelecaller ? UserLayout : Layout;
  const queryClient = useQueryClient();

  const [view, setView] = useState("list");
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [type, setType] = useState("");
  const [employee, setEmployee] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [month, setMonth] = useState(() => startOfMonth(new Date()));

  const [modal, setModal] = useState(null); // { mode, visit }
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const tabDef = STATUS_TABS.find((t) => t.value === tab) || STATUS_TABS[0];
  const [from, to] = rangeFor(dateRange);

  const listParams = {
    page,
    limit: pageSize,
    ...(tabDef.statuses.length ? { status: tabDef.statuses.join(",") } : {}),
    ...(type ? { type } : {}),
    ...(employee ? { assignedTo: employee } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(from ? { from: from.toISOString(), to: to.toISOString() } : {}),
    ...(tab === "upcoming" || tab === "pending" ? { sort: "asc" } : {}),
  };

  const { data: listData, isLoading } = useQuery({
    queryKey: ["visits", listParams],
    queryFn: async () => (await visitsAPI.list(listParams)).data,
    enabled: view === "list",
    placeholderData: keepPreviousData,
  });

  const calendarRange = useMemo(() => {
    const start = startOfWeek(month, { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    return { start, end };
  }, [month]);

  const { data: calendarVisits = [], isLoading: calendarLoading } = useQuery({
    queryKey: ["visits-calendar", calendarRange.start.toISOString(), employee],
    queryFn: async () =>
      (
        await visitsAPI.calendar({
          start: calendarRange.start.toISOString(),
          end: endOfDay(calendarRange.end).toISOString(),
          ...(employee ? { assignedTo: employee } : {}),
        })
      ).data?.data || [],
    enabled: view === "calendar",
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users", "visit-filter"],
    queryFn: async () => (await usersAPI.getAll({ limit: 500 })).data?.data || [],
    enabled: isAdmin,
    staleTime: 60000,
  });

  const employeeOptions = useMemo(
    () => [{ value: "", label: "All Employees" }, ...users.map((u) => ({ value: String(u._id), label: fullName(u) }))],
    [users]
  );

  const visits = listData?.data || [];
  const counts = listData?.counts || {};
  const pagination = listData?.pagination || { total: 0, pages: 0 };

  const tabs = STATUS_TABS.filter((t) => isAdmin || !t.adminOnly).map((t) => ({
    value: t.value,
    label: t.label,
    count: t.statuses.length
      ? t.statuses.reduce((sum, s) => sum + (counts[s] || 0), 0)
      : Object.values(counts).reduce((a, b) => a + b, 0),
  }));

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["visits"] });
    queryClient.invalidateQueries({ queryKey: ["visits-calendar"] });
    queryClient.invalidateQueries({ queryKey: ["visits-pending-count"] });
  };

  const resetPage = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  return (
    <PageLayout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
              {isTelecaller ? "My Visits" : "Visits & Meetings"}
            </h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">
              {isTelecaller
                ? "Your site visits and customer meetings"
                : "Schedule, approve and track site visits and customer meetings"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-secondary-200 bg-white p-0.5" role="group" aria-label="View">
              {[
                ["list", HiOutlineViewList, "List"],
                ["calendar", HiOutlineCalendar, "Calendar"],
              ].map(([key, Icon, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setView(key)}
                  aria-pressed={view === key}
                  className={clsx(
                    "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors",
                    view === key ? "bg-primary-600 text-white" : "text-secondary-600 hover:bg-secondary-50"
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
            <Button icon={<HiOutlinePlus className="w-4 h-4" />} onClick={() => setModal({ mode: "create" })}>
              <span className="hidden sm:inline">Schedule Visit</span>
              <span className="sm:hidden">Schedule</span>
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card padding="p-3">
          <div className="flex flex-wrap gap-2">
            {view === "list" && (
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search customer name or phone..."
                className="flex-1 min-w-[200px]"
              />
            )}
            {isAdmin && (
              <SearchableSelect
                options={employeeOptions}
                value={employee}
                onChange={(e) => resetPage(setEmployee)(e.target.value)}
                placeholder="All Employees"
                searchPlaceholder="Search employee..."
                className="min-w-[180px] flex-1 sm:flex-none"
              />
            )}
            {view === "list" && (
              <>
                <Select
                  options={[{ value: "", label: "All Types" }, ...VISIT_TYPES.map((t) => ({ value: t.value, label: t.label }))]}
                  value={type}
                  onChange={(e) => resetPage(setType)(e.target.value)}
                  className="min-w-[150px] flex-1 sm:flex-none"
                />
                <Select
                  options={DATE_RANGES}
                  value={dateRange}
                  onChange={(e) => resetPage(setDateRange)(e.target.value)}
                  className="min-w-[150px] flex-1 sm:flex-none"
                />
              </>
            )}
          </div>
        </Card>

        {view === "list" ? (
          <Card padding="p-0" className="overflow-hidden">
            <div className="px-3 pt-2">
              <Tabs tabs={tabs} activeTab={tab} onChange={resetPage(setTab)} />
            </div>
            {isLoading ? (
              <div className="flex justify-center py-16">
                <Spinner size="lg" />
              </div>
            ) : visits.length === 0 ? (
              <div className="py-8">
                <EmptyState
                  icon={<HiOutlineLocationMarker className="w-10 h-10" />}
                  title="No visits found"
                  description="Schedule a site visit or meeting with a customer, or adjust your filters"
                />
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-secondary-100">
                    <thead className="bg-secondary-50">
                      <tr>
                        {["Date & Time", "Customer", ...(isAdmin ? ["Employee"] : []), "Type", "Status", "Planned / Actual"].map((h) => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-bold text-secondary-600 uppercase tracking-wider whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                        <th className={clsx("px-4 py-3 text-right text-xs font-bold text-secondary-600 uppercase tracking-wider", stickyActionHeaderClass)}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-100 bg-white">
                      {visits.map((v, index) => {
                        const t = VISIT_TYPE_MAP[v.type] || VISIT_TYPE_MAP.site_visit;
                        const s = VISIT_STATUSES[v.status] || VISIT_STATUSES.scheduled;
                        const TIcon = t.icon;
                        return (
                          <tr key={v._id} className="hover:bg-primary-50/40 cursor-pointer" onClick={() => setDetailId(v._id)}>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <p className="text-sm font-semibold text-secondary-900">{format(new Date(v.scheduledStart), "dd MMM yyyy")}</p>
                              <p className="text-xs text-secondary-500">
                                {format(new Date(v.scheduledStart), "hh:mm a")} – {format(new Date(v.scheduledEnd), "hh:mm a")}
                              </p>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm font-semibold text-secondary-900">{v.leadId?.name || "—"}</p>
                              <p className="text-xs text-secondary-500">{v.leadId?.phone}</p>
                            </td>
                            {isAdmin && <td className="px-4 py-3 text-sm text-secondary-700 whitespace-nowrap">{fullName(v.assignedTo) || "—"}</td>}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="inline-flex items-center gap-1.5 text-sm text-secondary-700">
                                <TIcon className="w-4 h-4" style={{ color: t.color }} />
                                {t.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <Badge variant={s.variant}>{s.label}</Badge>
                            </td>
                            <td className="px-4 py-3 text-sm text-secondary-700 whitespace-nowrap">
                              {formatMinutes(plannedMinutes(v))}
                              <span className="text-secondary-400"> / </span>
                              {v.status === "completed" ? formatMinutes(v.actualMinutes) : "—"}
                            </td>
                            <td className={clsx("px-4 py-3 text-right", stickyActionCellClass)} onClick={(e) => e.stopPropagation()}>
                              {/* Opens to the left: above/below it would be clipped by the
                                  table's scroll container (single button at the right edge) */}
                              <Tooltip position="left" text="View details">
                                <button
                                  type="button"
                                  aria-label="View visit"
                                  onClick={() => setDetailId(v._id)}
                                  className="p-2 rounded-lg text-secondary-500 hover:text-primary-600 hover:bg-primary-50"
                                >
                                  <HiOutlineEye className="w-4 h-4" />
                                </button>
                              </Tooltip>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-secondary-100">
                  {visits.map((v) => {
                    const t = VISIT_TYPE_MAP[v.type] || VISIT_TYPE_MAP.site_visit;
                    const s = VISIT_STATUSES[v.status] || VISIT_STATUSES.scheduled;
                    const TIcon = t.icon;
                    return (
                      <button key={v._id} type="button" onClick={() => setDetailId(v._id)} className="w-full text-left px-4 py-3 hover:bg-primary-50/40">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-secondary-900 truncate">{v.leadId?.name || "—"}</p>
                            <p className="text-xs text-secondary-500 inline-flex items-center gap-1">
                              <TIcon className="w-3.5 h-3.5" style={{ color: t.color }} />
                              {t.label}
                              {isAdmin && fullName(v.assignedTo) && <> · {fullName(v.assignedTo)}</>}
                            </p>
                          </div>
                          <Badge variant={s.variant}>{s.label}</Badge>
                        </div>
                        <p className="text-xs text-secondary-600 mt-1">
                          {format(new Date(v.scheduledStart), "dd MMM, hh:mm a")} – {format(new Date(v.scheduledEnd), "hh:mm a")}
                        </p>
                      </button>
                    );
                  })}
                </div>

                <Pagination
                  currentPage={page}
                  totalPages={pagination.pages}
                  totalItems={pagination.total}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                  className="border-t border-secondary-100 px-4 py-3"
                />
              </>
            )}
          </Card>
        ) : (
          <VisitsCalendar
            month={month}
            range={calendarRange}
            visits={calendarVisits}
            loading={calendarLoading}
            showEmployee={isAdmin}
            onPrev={() => setMonth((m) => addMonths(m, -1))}
            onNext={() => setMonth((m) => addMonths(m, 1))}
            onToday={() => setMonth(startOfMonth(new Date()))}
            onOpen={setDetailId}
          />
        )}
      </div>

      <VisitModal
        isOpen={!!modal}
        mode={modal?.mode}
        visit={modal?.visit}
        previousVisit={modal?.previousVisit}
        onClose={() => setModal(null)}
        onSaved={() => {
          refresh();
          if (modal?.visit) queryClient.invalidateQueries({ queryKey: ["visit", modal.visit._id] });
        }}
      />
      <VisitDetailModal
        visitId={detailId}
        onClose={() => setDetailId(null)}
        onChanged={refresh}
        onEdit={(v) => {
          setDetailId(null);
          setModal({ mode: "edit", visit: v });
        }}
        onReschedule={(v) => {
          setDetailId(null);
          setModal({ mode: "reschedule", visit: v });
        }}
        onScheduleNext={(v) => {
          setDetailId(null);
          setModal({ mode: "create", previousVisit: v });
        }}
        onOpenVisit={setDetailId}
      />
    </PageLayout>
  );
};

// Month grid; each day lists its visits as coloured chips
const VisitsCalendar = ({ month, range, visits, loading, showEmployee, onPrev, onNext, onToday, onOpen }) => {
  const days = eachDayOfInterval({ start: range.start, end: range.end });
  const byDay = useMemo(() => {
    const map = {};
    visits.forEach((v) => {
      const key = format(new Date(v.scheduledStart), "yyyy-MM-dd");
      (map[key] = map[key] || []).push(v);
    });
    return map;
  }, [visits]);

  return (
    <Card padding="p-0" className="overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-secondary-100">
        <div className="flex items-center gap-1">
          <button type="button" onClick={onPrev} aria-label="Previous month" className="p-1.5 rounded-lg hover:bg-secondary-100">
            <HiOutlineChevronLeft className="w-5 h-5" />
          </button>
          <button type="button" onClick={onNext} aria-label="Next month" className="p-1.5 rounded-lg hover:bg-secondary-100">
            <HiOutlineChevronRight className="w-5 h-5" />
          </button>
          <h2 className="ml-2 text-base font-bold text-secondary-900">{format(month, "MMMM yyyy")}</h2>
          {loading && <Spinner size="sm" className="ml-2" />}
        </div>
        <Button size="sm" variant="outline" onClick={onToday}>
          Today
        </Button>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[720px]">
          <div className="grid grid-cols-7 bg-secondary-50 border-b border-secondary-100">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} className="px-2 py-2 text-xs font-bold text-secondary-500 uppercase text-center">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const items = byDay[key] || [];
              return (
                <div
                  key={key}
                  className={clsx(
                    "min-h-[110px] border-b border-r border-secondary-100 p-1.5",
                    !isSameMonth(day, month) && "bg-secondary-50/60"
                  )}
                >
                  <div
                    className={clsx(
                      "text-xs font-semibold mb-1 w-6 h-6 flex items-center justify-center rounded-full",
                      isToday(day) ? "bg-primary-600 text-white" : isSameMonth(day, month) ? "text-secondary-700" : "text-secondary-400"
                    )}
                  >
                    {format(day, "d")}
                  </div>
                  <div className="space-y-1">
                    {items.slice(0, 4).map((v) => {
                      const s = VISIT_STATUSES[v.status] || VISIT_STATUSES.scheduled;
                      return (
                        <button
                          key={v._id}
                          type="button"
                          onClick={() => onOpen(v._id)}
                          className="w-full text-left rounded-md px-1.5 py-1 text-[11px] leading-tight text-white truncate hover:opacity-90"
                          style={{ backgroundColor: s.color }}
                          title={`${format(new Date(v.scheduledStart), "hh:mm a")} ${v.leadId?.name || ""} · ${s.label}`}
                        >
                          <span className="font-semibold">{format(new Date(v.scheduledStart), "h:mma").toLowerCase()}</span>{" "}
                          {v.leadId?.name || "Visit"}
                          {showEmployee && fullName(v.assignedTo) && <span className="opacity-80"> · {v.assignedTo.firstName}</span>}
                        </button>
                      );
                    })}
                    {items.length > 4 && <p className="text-[11px] text-secondary-500 px-1">+{items.length - 4} more</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 px-4 py-2.5 border-t border-secondary-100">
        {Object.entries(VISIT_STATUSES)
          .filter(([k]) => k !== "rescheduled")
          .map(([k, s]) => (
            <span key={k} className="inline-flex items-center gap-1.5 text-xs text-secondary-600">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
              {s.label}
            </span>
          ))}
      </div>
    </Card>
  );
};

export default VisitsList;
